import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ThreeOrb from '@/components/feature/ThreeOrb';
import useReveal from '@/hooks/useReveal';
import { useViewCounter } from '@/hooks/useViewCounter';

const services = [
  { icon: 'ri-code-s-slash-line', title: 'Website Development', desc: 'Business sites, e-commerce and custom web apps built for performance.', slug: 'website-development' },
  { icon: 'ri-robot-2-line', title: 'AI Agents & Automation', desc: 'AI agents, chatbots and workflow automation that scale operations.', slug: 'ai-automation' },
  { icon: 'ri-search-eye-line', title: 'SEO & Digital Marketing', desc: 'Technical SEO, content strategy and AI-search optimization.', slug: 'seo-marketing' },
  { icon: 'ri-film-line', title: 'Video Editing', desc: 'Promo, social and brand videos with a cinematic edge.', slug: 'video-editing' },
  { icon: 'ri-line-chart-line', title: 'Social Media Analytics', desc: 'Performance tracking, audience insights and growth reporting.', slug: 'social-analytics' },
  { icon: 'ri-server-line', title: 'Hosting & Domain', desc: 'Domain, SSL, deployment, monitoring and maintenance.', slug: 'hosting' },
  { icon: 'ri-megaphone-line', title: 'Meta Ads', desc: 'Facebook & Instagram campaigns, targeting and optimization.', slug: 'meta-ads' },
];

const whyUs = [
  { icon: 'ri-flashlight-line', title: 'Modern Technology', desc: 'Next-gen stacks — React, Node, Python, Postgres.' },
  { icon: 'ri-sparkling-2-line', title: 'AI-Powered', desc: 'Automation and intelligence baked into everything.' },
  { icon: 'ri-puzzle-line', title: 'Custom Development', desc: 'No templates. Every solution shaped around you.' },
  { icon: 'ri-stack-line', title: 'Scalable Systems', desc: 'Architected to grow with your business.' },
  { icon: 'ri-speed-up-line', title: 'Performance Focused', desc: 'Speed, Core Web Vitals and clean code first.' },
  { icon: 'ri-briefcase-4-line', title: 'Business-Oriented', desc: 'Tech decisions that drive real revenue.' },
];

const process = [
  { n: '01', t: 'Discover', d: 'Understand goals, users and constraints.' },
  { n: '02', t: 'Plan', d: 'Architecture, flow and design system.' },
  { n: '03', t: 'Build', d: 'Development, integrations and QA.' },
  { n: '04', t: 'Launch', d: 'Deploy, monitor and optimize.' },
  { n: '05', t: 'Grow', d: 'Iterate with data and automation.' },
];

import { useState, useEffect } from 'react';
import { fetchProjects, type Project } from '@/lib/api';

const defaultProjects: Project[] = [
  { id: '1', name: 'NovaBank Platform', category: 'FinTech · Web App', image: 'https://readdy.ai/api/search-image?query=Futuristic%20clean%20dark%20fintech%20dashboard%20interface%20floating%20on%20a%20dark%20navy%20background%20with%20soft%20cyan%20and%20violet%20neon%20glow%2C%20sleek%20glassmorphism%20panels%2C%20elegant%20data%20visualization%2C%20minimal%20premium%20tech%20aesthetic%2C%20high%20detail&width=900&height=600&seq=fc-tech-proj1&orientation=landscape', featured: true },
  { id: '2', name: 'Atlas AI Agent', category: 'AI · Automation', image: 'https://readdy.ai/api/search-image?query=Futuristic%20clean%20AI%20assistant%20hologram%20with%20soft%20cyan%20neural%20network%20on%20a%20dark%20navy%20background%2C%20elegant%20glassmorphism%20interface%2C%20subtle%20violet%20glow%2C%20minimal%20premium%20tech%20aesthetic%2C%20high%20detail&width=900&height=600&seq=fc-tech-proj2&orientation=landscape', featured: true },
  { id: '3', name: 'Orbit Commerce', category: 'E-commerce · Web', image: 'https://readdy.ai/api/search-image?query=Futuristic%20clean%20e-commerce%20interface%20with%20floating%20product%20cards%20on%20a%20dark%20navy%20background%2C%20soft%20cyan%20and%20violet%20neon%20glow%2C%20elegant%20glassmorphism%20panels%2C%20minimal%20premium%20tech%20aesthetic%2C%20high%20detail&width=900&height=600&seq=fc-tech-proj3&orientation=landscape', featured: true },
];

export default function Home() {
  useReveal();
  useViewCounter();

  const [projectsList, setProjectsList] = useState<Project[]>(defaultProjects);

  useEffect(() => {
    let isMounted = true;
    const loadHomeProjects = () => {
      fetchProjects().then((data) => {
        if (isMounted && data && data.length > 0) {
          const featured = data.filter((p) => p.featured);
          const display = featured.length >= 3 ? featured.slice(0, 3) : data.slice(0, 3);
          setProjectsList(display);
        }
      });
    };

    loadHomeProjects();

    window.addEventListener('projects_updated', loadHomeProjects);
    window.addEventListener('storage', loadHomeProjects);

    return () => {
      isMounted = false;
      window.removeEventListener('projects_updated', loadHomeProjects);
      window.removeEventListener('storage', loadHomeProjects);
    };
  }, []);

  return (
    <div className="relative bg-ink-950 text-white min-h-screen max-w-full overflow-x-hidden">
      <Navbar />

      {/* HERO — Clean Futuristic Responsive */}
      <section className="relative pt-[clamp(105px,8.5vw,135px)] pb-16 sm:pb-20 md:pb-24 overflow-hidden w-full">
        <img
          src="https://readdy.ai/api/search-image?query=Futuristic%20clean%20abstract%20technology%20background%20with%20soft%20flowing%20cyan%20and%20violet%20light%20waves%20on%20a%20deep%20dark%20navy%20canvas%2C%20subtle%20geometric%20particles%2C%20elegant%20minimal%20premium%20tech%20aesthetic%2C%20cinematic%20wide%20composition%2C%20smooth%20gradients&width=1800&height=1200&seq=fc-tech-hero&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/80 via-ink-950/70 to-ink-950" />
        <div className="absolute inset-0 grid-bg opacity-30" />

        <div className="absolute right-0 top-0 h-full w-full md:w-1/2 pointer-events-none hidden md:block">
          <ThreeOrb />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 md:px-8 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border border-neon-400/30 bg-neon-400/10 text-neon-300 text-xs tracking-widest uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-neon-400 animate-pulse" />
              Digital Innovation Agency
            </div>

            <h1 className="mt-5 font-display text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] tracking-tight">
              We build intelligent<br className="hidden sm:block" />
              <span className="text-gradient"> digital products</span> that<br className="hidden sm:block" />
              move your business forward.
            </h1>

            <p className="mt-5 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
              FormaCode Technologies designs, builds and scales premium web platforms, AI-powered automation and growth systems for ambitious teams.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/contact" className="btn-primary text-white font-semibold px-7 py-3.5 text-sm whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-lg">
                Start Your Project
                <i className="ri-arrow-right-line" />
              </Link>
              <Link to="/services" className="glass text-white/90 font-semibold px-7 py-3.5 text-sm whitespace-nowrap inline-flex items-center justify-center gap-2 transition border border-white/10 rounded-lg hover:border-neon-400/50">
                Explore Services
              </Link>
            </div>

            <div className="mt-10 sm:mt-12 grid grid-cols-3 gap-3 sm:gap-8 max-w-lg">
              {[
                { n: '120+', l: 'Projects Delivered' },
                { n: '45+', l: 'AI Solutions' },
                { n: '98%', l: 'Client Satisfaction' },
              ].map((stat) => (
                <div key={stat.l}>
                  <div className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">{stat.n}</div>
                  <div className="text-[11px] sm:text-xs text-white/50 mt-1 leading-tight">{stat.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TECH TICKER */}
      <div className="relative border-y border-white/5 bg-ink-900/60 overflow-hidden py-4">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((k) => (
            <div key={k} className="flex items-center gap-8 sm:gap-12 pr-8 sm:pr-12">
              {[
                'Next.js', 'React', 'TypeScript', 'AI Agents', 'Python',
                'Automation', 'SEO', 'Tailwind CSS', 'Node.js', 'PostgreSQL',
                'Supabase', 'Meta Ads', 'LLM Integration',
              ].map((item) => (
                <span key={item} className="flex items-center gap-2.5 text-xs sm:text-sm font-mono text-white/40 whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-neon-400/60" /> {item}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ABOUT */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="mx-auto max-w-7xl grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div className="reveal">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-neon-400/30 bg-neon-400/10 text-neon-300 text-xs tracking-widest uppercase">
              About Us
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
              Technology built for<br className="hidden sm:block" />
              <span className="text-gradient"> real impact</span>
            </h2>
            <p className="mt-5 text-white/60 leading-relaxed text-sm sm:text-base">
              FormaCode Technologies is a team of engineers, designers and strategists focused on building intelligent digital systems that deliver measurable results.
            </p>
            <p className="mt-3 text-white/50 leading-relaxed text-sm sm:text-base">
              From custom web platforms to AI-powered automation, we combine thoughtful design with robust engineering to create products your business can rely on.
            </p>
            <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-neon-300 font-semibold text-xs sm:text-sm hover:gap-3 transition-all uppercase tracking-wider">
              Learn More <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="relative reveal">
            <div className="absolute -inset-6 rounded-3xl bg-neon-500/10 blur-3xl" />
            <img
              src="https://readdy.ai/api/search-image?query=Futuristic%20clean%20abstract%20geometric%20glass%20structure%20with%20floating%20translucent%20panels%20on%20a%20dark%20navy%20background%2C%20soft%20cyan%20and%20violet%20neon%20glow%2C%20elegant%20minimal%20premium%20tech%20aesthetic%2C%20high%20detail&width=1200&height=1000&seq=fc-tech-about&orientation=portrait"
              alt="FormaCode approach"
              className="relative w-full max-h-96 md:max-h-full object-cover object-top rounded-2xl border border-white/10"
            />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="relative mx-auto max-w-7xl">
          <div className="max-w-2xl reveal">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-neon-400/30 bg-neon-400/10 text-neon-300 text-xs tracking-widest uppercase">
              What We Do
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
              Our <span className="text-gradient">Services</span>
            </h2>
            <p className="mt-3 text-white/50 text-sm sm:text-base">End-to-end digital capabilities, designed to work together seamlessly.</p>
          </div>

          <div className="mt-10 sm:mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => (
              <Link
                key={s.title}
                to={`/services/${s.slug}`}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-neon-400/40 hover:-translate-y-1 transition-all duration-500 p-6 sm:p-7 reveal block"
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-neon-600 to-neon-400 text-white text-xl group-hover:shadow-[0_0_25px_rgba(139,92,246,0.4)] transition-shadow">
                  <i className={s.icon} />
                </div>
                <h3 className="mt-4 font-display font-bold text-base sm:text-lg text-white group-hover:text-neon-300 transition-colors">{s.title}</h3>
                <p className="mt-2 text-xs sm:text-sm text-white/50 leading-relaxed">{s.desc}</p>
                <div className="mt-4 inline-flex items-center gap-2 text-xs text-neon-300 opacity-0 group-hover:opacity-100 transition font-semibold uppercase">
                  Learn More <i className="ri-arrow-right-up-line" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="mx-auto max-w-4xl">
          <div className="text-center reveal">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-neon-400/30 bg-neon-400/10 text-neon-300 text-xs tracking-widest uppercase">
              Why Choose Us
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
              Built to <span className="text-gradient">outperform</span>
            </h2>
          </div>

          <div className="mt-10 sm:mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyUs.map((w, i) => (
              <div key={w.title} className="group rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-neon-400/40 transition-all p-5 reveal" style={{ transitionDelay: `${i * 50}ms` }}>
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-neon-400/15 text-neon-300 text-lg shrink-0 mb-3 group-hover:shadow-[0_0_20px_rgba(139,92,246,0.3)] transition-shadow">
                  <i className={w.icon} />
                </div>
                <h3 className="font-display font-bold text-sm sm:text-base text-white group-hover:text-neon-300 transition-colors">{w.title}</h3>
                <p className="mt-1.5 text-xs sm:text-sm text-white/50 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="relative mx-auto max-w-7xl">
          <div className="text-center reveal">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-neon-400/30 bg-neon-400/10 text-neon-300 text-xs tracking-widest uppercase">
              Our Process
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-bold">
              <span className="text-gradient">How we work</span>
            </h2>
          </div>

          <div className="mt-10 sm:mt-14 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6">
            {process.map((p, i) => (
              <div key={p.n} className="relative text-center reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="mx-auto w-14 h-14 rounded-2xl glass flex items-center justify-center font-display font-bold text-neon-300 border border-neon-400/20 text-base">
                  {p.n}
                </div>
                <h4 className="mt-3 font-display font-bold text-white text-sm sm:text-base">{p.t}</h4>
                <p className="mt-1 text-xs text-white/40 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="relative py-12 sm:py-16 md:py-24 px-4 sm:px-6">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 reveal">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-neon-400/30 bg-neon-400/10 text-neon-300 text-xs tracking-widest uppercase">
                Featured Work
              </div>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
                Selected <span className="text-gradient">projects</span>
              </h2>
            </div>
            <Link to="/portfolio" className="inline-flex items-center gap-2 text-neon-300 text-xs sm:text-sm font-semibold hover:gap-3 transition-all uppercase tracking-wider">
              View All Work <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="mt-10 sm:mt-12 grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {projectsList.map((p, i) => (
              <Link key={p.id || p.name} to="/portfolio" className="group reveal relative overflow-hidden rounded-2xl border border-white/10 hover:border-neon-400/40 transition-all block" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.image || ''} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/60 to-transparent" />
                <div className="absolute bottom-0 p-5 sm:p-6">
                  <div className="text-[11px] text-neon-300 font-mono tracking-widest uppercase">{p.category || 'Websites'}</div>
                  <h3 className="mt-1 font-display font-bold text-lg sm:text-xl text-white group-hover:text-neon-300 transition-colors">{p.name}</h3>
                  <div className="mt-2.5 inline-flex items-center gap-2 text-xs text-white/70 font-semibold uppercase">
                    View Case Study <i className="ri-arrow-right-up-line" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="relative mx-auto max-w-4xl text-center reveal">
          <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-bold leading-tight">
            Let's build something<br className="hidden sm:block" />
            <span className="text-gradient"> remarkable together</span>
          </h2>
          <p className="mt-5 text-white/60 text-base sm:text-lg max-w-2xl mx-auto">
            Ready to transform your business with intelligent technology? Let's talk.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary text-white font-semibold px-8 py-3.5 text-sm whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-lg">
              Get In Touch
              <i className="ri-arrow-right-line" />
            </Link>
            <a href="mailto:formacodetech@gmail.com" className="glass text-white/90 font-semibold px-7 py-3.5 text-sm whitespace-nowrap inline-flex items-center justify-center gap-2 transition border border-white/10 rounded-lg hover:border-neon-400/50">
              <i className="ri-mail-send-line" />
              formacodetech@gmail.com
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}